import { createReference, MedplumClient, PatchOperation, resolveId } from '@medplum/core';
import { Reference, Task } from '@medplum/fhirtypes';
import { TaskGroupIdentifier, TaskStatus } from './constants';
import { TASK_COMPLETED_BY_EXTENSION } from './extensions';

/**
 * Changes the status of a notification Task.
 *
 * CLINICAL tasks (group-identifier orbit-provider-task) surfaced in the
 * inbox get special handling — their statuses are workflow state, not
 * read-state:
 * - read/unread toggles (open ↔ open) are a NO-OP;
 * - archive (→ completed) and unarchive (→ ready) apply the full guarded
 *   completion protocol (versionId test, lastModified, completed-by stamp
 *   on complete / stamp cleared on reopen), so an inbox action is
 *   indistinguishable from the patient tab's Complete/Undo. On a version
 *   conflict the task is skipped rather than failing the whole bulk action.
 */
export async function changeNotificationStatus(
  medplum: MedplumClient,
  notificationRef: Reference<Task>,
  status: TaskStatus,
  /**
   * The already-loaded Task, when the caller has it (every inbox surface
   * does). Skips a read per row — a 30-row bulk archive was issuing 30 reads
   * on top of 30 patches purely to discriminate clinical vs notification.
   */
  knownTask?: Task
): Promise<Task> {
  const id = resolveId(notificationRef) || '';
  const task = knownTask ?? (await medplum.readResource('Task', id));

  if (task.groupIdentifier?.value !== TaskGroupIdentifier.ORBIT_PROVIDER_TASK) {
    return medplum.patchResource('Task', id, [{ op: 'replace', path: '/status', value: status }]);
  }

  const isOpen = task.status === TaskStatus.READY || task.status === TaskStatus.IN_PROGRESS;
  const targetsOpen = status === TaskStatus.READY || status === TaskStatus.IN_PROGRESS;
  if (isOpen && targetsOpen) {
    return task;
  }

  const now = new Date().toISOString();
  const ops: PatchOperation[] = [
    { op: 'test', path: '/meta/versionId', value: task.meta?.versionId },
    { op: task.lastModified !== undefined ? 'replace' : 'add', path: '/lastModified', value: now },
    { op: 'replace', path: '/status', value: status },
  ];
  if (status === TaskStatus.COMPLETED) {
    const profile = medplum.getProfile();
    if (profile) {
      // Append to the existing extensions rather than replacing them: a
      // wholesale replace silently dropped any other extension on the task.
      const others = (task.extension ?? []).filter((e) => e.url !== TASK_COMPLETED_BY_EXTENSION);
      ops.push({
        op: task.extension !== undefined ? 'replace' : 'add',
        path: '/extension',
        value: [...others, { url: TASK_COMPLETED_BY_EXTENSION, valueReference: createReference(profile) }],
      });
    }
  } else if (task.extension?.some((e) => e.url === TASK_COMPLETED_BY_EXTENSION)) {
    // Filtered rebuild, not a wholesale remove: any other extension on the
    // task must survive a reopen.
    const remaining = task.extension.filter((e) => e.url !== TASK_COMPLETED_BY_EXTENSION);
    ops.push(
      remaining.length > 0
        ? { op: 'replace', path: '/extension', value: remaining }
        : { op: 'remove', path: '/extension' }
    );
  }

  try {
    return await medplum.patchResource('Task', id, ops);
  } catch {
    // Concurrent change — leave this task as-is; the refreshed list shows truth.
    return task;
  }
}

export async function markNotificationAsRead(
  medplum: MedplumClient,
  notificationRef: Reference<Task>,
  knownTask?: Task
): Promise<Task> {
  return changeNotificationStatus(medplum, notificationRef, TaskStatus.IN_PROGRESS, knownTask);
}

export async function markNotificationAsUnread(
  medplum: MedplumClient,
  notificationRef: Reference<Task>
): Promise<Task> {
  return changeNotificationStatus(medplum, notificationRef, TaskStatus.READY);
}

/**
 * Marks multiple notifications as read
 * @param medplum - The Medplum client instance
 * @param notificationRefs - Array of references to notifications to mark as read
 * @returns Promise that resolves to an array of updated Communication resources
 */
export async function markNotificationsAsRead(
  medplum: MedplumClient,
  notificationRefs: Reference<Task>[]
): Promise<Task[]> {
  const operations = notificationRefs.map(async (ref) => {
    return await markNotificationAsRead(medplum, ref);
  });

  return await Promise.all(operations);
}

/**
 * Marks multiple notifications as unread
 * @param medplum - The Medplum client instance
 * @param notificationRefs - Array of references to notifications to mark as unread
 * @returns Promise that resolves to an array of updated Communication resources
 */
export async function markNotificationsAsUnread(
  medplum: MedplumClient,
  notificationRefs: Reference<Task>[]
): Promise<Task[]> {
  const operations = notificationRefs.map(async (ref) => {
    return await markNotificationAsUnread(medplum, ref);
  });

  return await Promise.all(operations);
}

/**
 * Marks a notification as archived
 * @param medplum - The Medplum client instance
 * @param notificationRef - The reference to the notification (Communication resource) to mark as archived
 * @returns Promise that resolves to the updated Communication resource
 */
export async function markNotificationAsArchived(
  medplum: MedplumClient,
  notificationRef: Reference<Task>
): Promise<Task> {
  return changeNotificationStatus(medplum, notificationRef, TaskStatus.COMPLETED);
}

/**
 * Marks multiple notifications as archived
 * @param medplum - The Medplum client instance
 * @param notificationRefs - Array of references to notifications to mark as archived
 * @returns Promise that resolves to an array of updated Communication resources
 */
export async function markNotificationsAsArchived(
  medplum: MedplumClient,
  notificationRefs: Reference<Task>[]
): Promise<Task[]> {
  const operations = notificationRefs.map(async (ref) => {
    return await markNotificationAsArchived(medplum, ref);
  });

  return await Promise.all(operations);
}

/**
 * Moves a notification to inbox (unread status)
 * @param medplum - The Medplum client instance
 * @param notificationRef - The reference to the notification (Communication resource) to move to inbox
 * @returns Promise that resolves to the updated Communication resource
 */
export async function moveNotificationToInbox(medplum: MedplumClient, notificationRef: Reference<Task>): Promise<Task> {
  return changeNotificationStatus(medplum, notificationRef, TaskStatus.READY);
}

/**
 * Moves multiple notifications to inbox (unread status)
 * @param medplum - The Medplum client instance
 * @param notificationRefs - Array of references to notifications to move to inbox
 * @returns Promise that resolves to an array of updated Communication resources
 */
export async function moveNotificationsToInbox(
  medplum: MedplumClient,
  notificationRefs: Reference<Task>[]
): Promise<Task[]> {
  const operations = notificationRefs.map(async (ref) => {
    return await moveNotificationToInbox(medplum, ref);
  });

  return await Promise.all(operations);
}
